import {
  AccountInfo,
  AuthError,
  BrowserAuthError,
  InteractionRequiredAuthError,
} from '@azure/msal-browser';
import { requestLoginRedirect } from 'services/authentication/loginRedirect';
import {
  ACCESS_TOKEN_SCOPES,
  MSAL_REDIRECT_PARAMETERS,
  msalInstance,
  PASSWORD_RESET_REDIRECT_PARAMETERS,
} from 'services/authentication/msalConfig';
import { logger } from 'services/logs/logger';

/**
 * Redirects the user into the Azure AD B2C self-service password reset flow.
 */
export const redirectToPasswordReset = (): Promise<boolean> => {
  // logger.logInfo('Redirecting to B2C password reset flow');
  return requestLoginRedirect(PASSWORD_RESET_REDIRECT_PARAMETERS);
};

/**
 * Clears MSAL cache from localStorage to prevent infinite login loops
 */
const clearMsalCache = () => {
  try {
    // Clear all MSAL cache entries from localStorage
    // Azure B2C keys include: client ID GUIDs, b2clogin.com, policy names (b2c_1, b2c_1a, etc.)
    const msalKeys = Object.keys(localStorage).filter(
      (key) =>
        key.startsWith('msal.') ||
        key.includes('login.windows') ||
        key.includes('b2clogin.com') ||
        key.includes('-b2c_1') || // Matches b2c_1, b2c_1a, b2c_1_signup, etc.
        key.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i), // Matches GUID-prefixed keys
    );

    msalKeys.forEach((key) => {
      localStorage.removeItem(key);
    });

    // logger.logInfo('MSAL cache cleared due to authentication error', {
    //   clearedKeys: msalKeys.length,
    // });
  } catch (error) {
    logger.logWarn('Failed to clear MSAL cache', error);
  }
};

/**
 * Validates if an account is valid and not expired
 */
const isAccountValid = (account: AccountInfo | null): boolean => {
  if (!account) return false;

  // Check if account has required claims
  if (!account.idTokenClaims) return false;

  // Check if account session is not expired
  // idTokenClaims.exp contains expiration timestamp
  const expirationTime = (account.idTokenClaims as { exp?: number } | undefined)?.exp;
  if (expirationTime && Date.now() / 1000 > expirationTime) {
    return false;
  }

  return true;
};

/**
 * Transient browser-side failures. Clearing the cache or redirecting on these
 * would wipe still valid tokens and turn a temporary race into a login loop.
 */
const TRANSIENT_BROWSER_ERROR_CODES = [
  'interaction_in_progress',
  'no_network_connectivity',
  'post_request_failed',
];

/**
 * Classifies authentication errors and determines appropriate action
 */
const classifyAuthError = (
  error: unknown,
): {
  shouldClearCache: boolean;
  shouldRedirect: boolean;
  errorType: string;
} => {
  // InteractionRequiredAuthError - requires user interaction
  if (error instanceof InteractionRequiredAuthError) {
    // Specific handling for AADB2C90077
    const isB2CSessionError =
      error.errorCode === 'interaction_required' && error.errorMessage?.includes('AADB2C90077');

    return {
      shouldClearCache: isB2CSessionError, // Clear cache for B2C session errors
      shouldRedirect: true,
      errorType: 'InteractionRequired',
    };
  }

  // BrowserAuthError - browser-specific issues
  if (error instanceof BrowserAuthError) {
    const isTransient = TRANSIENT_BROWSER_ERROR_CODES.includes(error.errorCode);

    return {
      shouldClearCache: !isTransient, // Clear cache for non recoverable browser auth errors
      shouldRedirect: !isTransient,
      errorType: 'BrowserAuth',
    };
  }

  // Generic AuthError
  if (error instanceof AuthError) {
    return {
      shouldClearCache: false,
      shouldRedirect: true,
      errorType: 'Auth',
    };
  }

  // Unknown error type
  return {
    shouldClearCache: false,
    shouldRedirect: true,
    errorType: 'Unknown',
  };
};

export const acquireAccessToken = async (): Promise<string | undefined> => {
  const accounts = msalInstance.getAllAccounts();

  // No accounts found - redirect to login
  if (accounts.length === 0) {
    // logger.logInfo('No accounts found, redirecting to login');
    void requestLoginRedirect(MSAL_REDIRECT_PARAMETERS);
    return undefined;
  }

  // Multiple accounts found - log warning and use first account
  if (accounts.length > 1) {
    logger.logWarn('Multiple accounts found, using first account', {
      accountCount: accounts.length,
    });
  }

  const account = accounts[0];

  // Validate account before attempting silent acquisition
  if (!isAccountValid(account)) {
    logger.logWarn('Account validation failed, clearing cache and redirecting', {
      accountId: account.homeAccountId,
    });
    clearMsalCache();
    void requestLoginRedirect(MSAL_REDIRECT_PARAMETERS);
    return undefined;
  }

  try {
    // logger.logInfo('Attempting silent token acquisition', {
    //   accountId: account.homeAccountId,
    // });

    const authResult = await msalInstance.acquireTokenSilent({
      scopes: ACCESS_TOKEN_SCOPES,
      account: account,
      forceRefresh: false, // Use cached token if valid
    });

    // logger.logInfo('Token acquired successfully');
    return authResult.accessToken;
  } catch (error) {
    // Classify the error and determine action
    const { shouldClearCache, shouldRedirect, errorType } = classifyAuthError(error);

    // Log detailed error information
    logger.logError('Error during Azure AD token acquisition', {
      errorType,
      errorCode: (error as { errorCode?: string }).errorCode,
      errorMessage: (error as { errorMessage?: string }).errorMessage,
      shouldClearCache,
      shouldRedirect,
      accountId: account.homeAccountId,
    });

    // Clear cache if needed (prevents infinite loop)
    if (shouldClearCache) {
      logger.logWarn('Clearing MSAL cache due to error', {
        errorType,
        errorCode: (error as { errorCode?: string }).errorCode,
      });
      clearMsalCache();
    }

    // Redirect to login if needed
    if (shouldRedirect) {
      // logger.logInfo('Redirecting to login due to authentication error');
      void requestLoginRedirect(MSAL_REDIRECT_PARAMETERS);
    }

    return undefined;
  }
};
